import type { CheckIn, ObjectiveStatusT } from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import { type WithNow, getCheckInsMap } from './doc.ts';
import { updateKeyResult, updateObjective } from './objectives.ts';

export interface CreateCheckInInput {
  cadence: 'daily' | 'weekly';
  targetType: 'objective' | 'key_result';
  targetId: string;
  note: string;
  blockers?: string;
  nextSteps?: string;
  statusUpdate?: ObjectiveStatusT;
  confidenceUpdate?: number;
  progressSnapshot?: number;
}

function timestamp(options: WithNow): number {
  return options.now ?? Date.now();
}

export function getCheckIn(doc: Y.Doc, id: string): CheckIn | undefined {
  const inner = getCheckInsMap(doc).get(id);
  if (!inner) return undefined;
  return checkInFromMap(id, inner);
}

export function listCheckIns(doc: Y.Doc): CheckIn[] {
  const checkIns: CheckIn[] = [];
  for (const [id, inner] of getCheckInsMap(doc).entries()) {
    checkIns.push(checkInFromMap(id, inner));
  }
  checkIns.sort((a, b) => b.createdAt - a.createdAt);
  return checkIns;
}

export function createCheckIn(
  doc: Y.Doc,
  input: CreateCheckInInput,
  options: WithNow = {},
): CheckIn {
  const now = timestamp(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  inner.set('cadence', input.cadence);
  inner.set('targetType', input.targetType);
  inner.set('targetId', input.targetId);
  inner.set('note', input.note);
  inner.set('createdAt', now);

  setOrDelete(inner, 'blockers', input.blockers);
  setOrDelete(inner, 'nextSteps', input.nextSteps);
  setOrDelete(inner, 'statusUpdate', input.statusUpdate);
  setOrDelete(
    inner,
    'confidenceUpdate',
    input.confidenceUpdate === undefined ? undefined : clampPercent(input.confidenceUpdate),
  );
  setOrDelete(
    inner,
    'progressSnapshot',
    input.progressSnapshot === undefined ? undefined : clampPercent(input.progressSnapshot),
  );

  doc.transact(() => {
    getCheckInsMap(doc).set(id, inner);
    if (input.targetType === 'objective') {
      if (input.statusUpdate !== undefined) {
        updateObjective(doc, input.targetId, { status: input.statusUpdate }, options);
      }
      return;
    }

    const patch: { status?: ObjectiveStatusT; confidence?: number } = {};
    if (input.statusUpdate !== undefined) patch.status = input.statusUpdate;
    if (input.confidenceUpdate !== undefined)
      patch.confidence = clampPercent(input.confidenceUpdate);
    if (Object.keys(patch).length > 0) {
      updateKeyResult(doc, input.targetId, patch, options);
    }
  });

  return checkInFromMap(id, inner);
}

function checkInFromMap(id: string, inner: Y.Map<unknown>): CheckIn {
  const checkIn: CheckIn = {
    id,
    cadence: (inner.get('cadence') ?? 'daily') as CheckIn['cadence'],
    targetType: (inner.get('targetType') ?? 'objective') as CheckIn['targetType'],
    targetId: String(inner.get('targetId') ?? ''),
    note: String(inner.get('note') ?? ''),
    createdAt: Number(inner.get('createdAt') ?? 0),
  };

  copyOptionalString(inner, checkIn, 'blockers');
  copyOptionalString(inner, checkIn, 'nextSteps');
  copyOptionalString(inner, checkIn, 'statusUpdate');
  copyOptionalNumber(inner, checkIn, 'confidenceUpdate');
  copyOptionalNumber(inner, checkIn, 'progressSnapshot');

  return checkIn;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function setOrDelete(inner: Y.Map<unknown>, key: string, value: unknown): void {
  if (value === undefined) inner.delete(key);
  else inner.set(key, value);
}

function copyOptionalString<K extends keyof CheckIn>(
  inner: Y.Map<unknown>,
  target: CheckIn,
  key: K,
): void {
  const value = inner.get(key as string);
  if (typeof value === 'string') {
    (target as Record<string, unknown>)[key as string] = value;
  }
}

function copyOptionalNumber<K extends keyof CheckIn>(
  inner: Y.Map<unknown>,
  target: CheckIn,
  key: K,
): void {
  const value = inner.get(key as string);
  if (typeof value === 'number') {
    (target as Record<string, unknown>)[key as string] = value;
  }
}
